import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Download, Filter, 
  Loader2, TrendingUp, Trash2,
  DollarSign, BarChart3, Building2
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import ConfirmModal from "../ui/ConfirmModal";
import { PageInfoUnderTabs } from "./PageInfo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper function to format month from "2026-01" to "Gennaio 2026"
const formatMonthToItalian = (monthStr) => {
  if (!monthStr) return monthStr;
  const monthNames = {
    '01': 'Gennaio', '02': 'Febbraio', '03': 'Marzo', '04': 'Aprile',
    '05': 'Maggio', '06': 'Giugno', '07': 'Luglio', '08': 'Agosto',
    '09': 'Settembre', '10': 'Ottobre', '11': 'Novembre', '12': 'Dicembre'
  };
  const parts = monthStr.split('-');
  if (parts.length === 2) {
    const year = parts[0];
    const month = parts[1];
    return `${monthNames[month] || month} ${year}`;
  }
  return monthStr;
};

// Report Tab Component with Delete functionality
function ReportTab({ 
  title, 
  description, 
  icon: Icon, 
  reportType, 
  getAuthHeaders,
  filters
}) {
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, ids: [] });
  const [deleting, setDeleting] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    setSelectedRows([]);
    try {
      const payload = {};
      if (filters.dateFrom && filters.dateTo) {
        payload.date_from = filters.dateFrom;
        payload.date_to = filters.dateTo;
      } else if (filters.year) {
        payload.year = parseInt(filters.year);
      }
      
      const res = await axios.post(
        `${API}/admin/reports/${reportType}`, 
        payload,
        getAuthHeaders()
      );
      setData(res.data.data || []);
      setTotals(res.data.totals || {});
    } catch (error) {
      console.error("Error loading report:", error);
      toast.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, [filters, reportType]);

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ report_type: reportType });
      
      const payload = {};
      if (filters.dateFrom && filters.dateTo) {
        payload.date_from = filters.dateFrom;
        payload.date_to = filters.dateTo;
      } else if (filters.year) {
        payload.year = parseInt(filters.year);
      }
      
      const res = await axios.post(
        `${API}/admin/reports/export-excel?${params.toString()}`,
        payload,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = res.headers['content-disposition'];
      let filename = `report_${reportType}_${filters.dateFrom || filters.year}_${filters.dateTo || filters.year}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report esportato con successo!");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Errore nell'esportazione del report");
    } finally {
      setExporting(false);
    }
  };

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  // Toggle all rows
  const toggleAllRows = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(data.map(row => getRowId(row)));
    }
  };

  // Get row ID
  const getRowId = (row) => {
    return row.request_id || row.id || row._id || row.booking_id || row.month;
  };

  // Delete single row
  const handleDeleteSingle = (row) => {
    const id = getRowId(row);
    setDeleteModal({ 
      isOpen: true, 
      type: 'single', 
      ids: [id],
      message: `Eliminare il record "${row.guest_name || row.month}"?`
    });
  };

  // Delete selected rows
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;
    setDeleteModal({ 
      isOpen: true, 
      type: 'bulk', 
      ids: selectedRows,
      message: `Eliminare ${selectedRows.length} record selezionati?`
    });
  };

  // Confirm delete
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.post(
        `${API}/admin/reports/delete`,
        { 
          report_type: reportType,
          ids: deleteModal.ids 
        },
        getAuthHeaders()
      );
      toast.success(`${deleteModal.ids.length} record eliminati`);
      setDeleteModal({ isOpen: false, type: null, ids: [] });
      setSelectedRows([]);
      loadReport();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Errore nell'eliminazione dei record");
    } finally {
      setDeleting(false);
    }
  };

  // Render table based on report type
  const renderTable = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-12 text-[#78716C]">
          <Icon size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessun dato per il periodo selezionato</p>
        </div>
      );
    }

    // Tassa di Soggiorno Table
    if (reportType === "tassa-soggiorno") {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F5F4]">
              <tr>
                <th className="w-10 py-3 px-2"></th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Ospite</th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Check-in</th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Check-out</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Notti</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Ospiti</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Paganti</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Esenti</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Tassa €</th>
                <th className="w-10 py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr key={rowId} className={`border-b border-[#E7E5E4] hover:bg-[#FAFAF9] ${selectedRows.includes(rowId) ? 'bg-[#FDF9EF]' : ''}`}>
                    <td className="py-3 px-2 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedRows.includes(rowId)}
                        onChange={() => toggleRowSelection(rowId)}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                    </td>
                    <td className="py-3 px-4">{row.guest_name}</td>
                    <td className="py-3 px-4">{row.check_in}</td>
                    <td className="py-3 px-4">{row.check_out}</td>
                    <td className="py-3 px-4 text-center">{row.nights}</td>
                    <td className="py-3 px-4 text-center">{row.total_guests}</td>
                    <td className="py-3 px-4 text-center">{row.paying_guests}</td>
                    <td className="py-3 px-4 text-center">{row.exempt_guests}</td>
                    <td className="py-3 px-4 text-right font-medium">€{row.tax_amount?.toFixed(2)}</td>
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => handleDeleteSingle(row)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#D4AF37]/10 font-semibold">
                <td className="py-3 px-2"></td>
                <td className="py-3 px-4" colSpan={3}>TOTALE</td>
                <td className="py-3 px-4 text-center">{totals.total_nights}</td>
                <td className="py-3 px-4 text-center">{totals.total_guests}</td>
                <td className="py-3 px-4 text-center">{totals.total_paying_guests}</td>
                <td className="py-3 px-4 text-center">{totals.total_exempt_guests}</td>
                <td className="py-3 px-4 text-right">€{totals.total_tax?.toFixed(2)}</td>
                <td className="py-3 px-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    // Importi Incassati Table
    if (reportType === "importi-incassati") {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F5F4]">
              <tr>
                <th className="w-10 py-3 px-2"></th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Ospite</th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Check-in</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Soggiorno</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Pulizie</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Tassa</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Base Imp.</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Cedolare</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Netto</th>
                <th className="w-10 py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr key={rowId} className={`border-b border-[#E7E5E4] hover:bg-[#FAFAF9] ${selectedRows.includes(rowId) ? 'bg-[#FDF9EF]' : ''}`}>
                    <td className="py-3 px-2 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedRows.includes(rowId)}
                        onChange={() => toggleRowSelection(rowId)}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                    </td>
                    <td className="py-3 px-4">{row.guest_name}</td>
                    <td className="py-3 px-4">{row.check_in}</td>
                    <td className="py-3 px-4 text-right">€{row.importo_soggiorno?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">€{row.spese_pulizia?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">€{row.tassa_soggiorno?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">€{row.base_imponibile?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-red-600">€{row.cedolare_secca?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">€{row.guadagno_netto?.toFixed(2)}</td>
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => handleDeleteSingle(row)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#D4AF37]/10 font-semibold">
                <td className="py-3 px-2"></td>
                <td className="py-3 px-4" colSpan={2}>TOTALE</td>
                <td className="py-3 px-4 text-right">€{totals.total_importo_soggiorno?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right">€{totals.total_spese_pulizia?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right">€{totals.total_tassa_soggiorno?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right">-</td>
                <td className="py-3 px-4 text-right text-red-600">€{totals.total_cedolare_secca?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right text-green-600">€{totals.total_guadagno_netto?.toFixed(2)}</td>
                <td className="py-3 px-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    // Occupazione Table
    if (reportType === "occupazione") {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F5F4]">
              <tr>
                <th className="w-10 py-3 px-2"></th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Mese</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Notti Disponibili</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Notti Occupate</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">Tasso Occupazione</th>
                <th className="w-10 py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr key={rowId} className={`border-b border-[#E7E5E4] hover:bg-[#FAFAF9] ${selectedRows.includes(rowId) ? 'bg-[#FDF9EF]' : ''}`}>
                    <td className="py-3 px-2 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedRows.includes(rowId)}
                        onChange={() => toggleRowSelection(rowId)}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                    </td>
                    <td className="py-3 px-4 font-medium">{formatMonthToItalian(row.month)}</td>
                    <td className="py-3 px-4 text-center">{row.available_nights}</td>
                    <td className="py-3 px-4 text-center">{row.occupied_nights}</td>
                    <td className="py-3 px-4 text-center">
                      <span className={`font-medium ${row.occupancy_rate >= 70 ? 'text-green-600' : row.occupancy_rate >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {row.occupancy_rate}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => handleDeleteSingle(row)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#D4AF37]/10 font-semibold">
                <td className="py-3 px-2"></td>
                <td className="py-3 px-4">TOTALE / MEDIA</td>
                <td className="py-3 px-4 text-center">{totals.total_available}</td>
                <td className="py-3 px-4 text-center">{totals.total_occupied}</td>
                <td className="py-3 px-4 text-center">{totals.average_rate}%</td>
                <td className="py-3 px-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    // Guadagni Table
    if (reportType === "guadagni") {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F5F5F4]">
              <tr>
                <th className="w-10 py-3 px-2"></th>
                <th className="py-3 px-4 text-left font-medium text-[#44403C]">Mese</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Importo Soggiorno</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Spese Pulizia</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Cedolare 21%</th>
                <th className="py-3 px-4 text-right font-medium text-[#44403C]">Guadagno Netto</th>
                <th className="py-3 px-4 text-center font-medium text-[#44403C]">N. Prenotazioni</th>
                <th className="w-10 py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const rowId = getRowId(row);
                return (
                  <tr key={rowId} className={`border-b border-[#E7E5E4] hover:bg-[#FAFAF9] ${selectedRows.includes(rowId) ? 'bg-[#FDF9EF]' : ''}`}>
                    <td className="py-3 px-2 text-center">
                      <input 
                        type="checkbox" 
                        checked={selectedRows.includes(rowId)}
                        onChange={() => toggleRowSelection(rowId)}
                        className="w-4 h-4 accent-[#D4AF37]"
                      />
                    </td>
                    <td className="py-3 px-4 font-medium">{formatMonthToItalian(row.month)}</td>
                    <td className="py-3 px-4 text-right">€{row.importo_soggiorno?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">€{row.spese_pulizia?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-red-600">€{row.cedolare_secca?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600">€{row.guadagno_netto?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-center">{row.bookings_count}</td>
                    <td className="py-3 px-2 text-center">
                      <button
                        onClick={() => handleDeleteSingle(row)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Elimina"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-[#D4AF37]/10 font-semibold">
                <td className="py-3 px-2"></td>
                <td className="py-3 px-4">TOTALE</td>
                <td className="py-3 px-4 text-right">€{totals.total_importo?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right">€{totals.total_pulizia?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right text-red-600">€{totals.total_cedolare?.toFixed(2)}</td>
                <td className="py-3 px-4 text-right text-green-600">€{totals.total_guadagno?.toFixed(2)}</td>
                <td className="py-3 px-4 text-center">{totals.total_bookings}</td>
                <td className="py-3 px-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header with description and action buttons - ALIGNED TO PAYMENTS STYLE */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-xl flex items-center gap-2">
              <Icon size={20} className="text-[#D4AF37]" />
              {title}
            </h3>
            <p className="text-sm text-[#78716C]">{description}</p>
          </div>
          
          {/* Select All checkbox - LIKE PAYMENTS */}
          {data.length > 0 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="checkbox" 
                checked={selectedRows.length === data.length && data.length > 0}
                onChange={toggleAllRows}
                className="w-4 h-4 accent-[#D4AF37]"
              />
              <span className="text-sm text-[#78716C]">Seleziona tutti ({data.length})</span>
            </label>
          )}
          
          {/* Single Delete Button - LIKE PAYMENTS */}
          {selectedRows.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDeleteSelected}
            >
              <Trash2 size={14} className="mr-1" />
              Elimina {selectedRows.length}
            </Button>
          )}
        </div>
        
        {/* Export Button */}
        <Button
          onClick={exportToExcel}
          disabled={exporting || data.length === 0}
          className="bg-[#D4AF37] hover:bg-[#B8972E] text-white"
        >
          {exporting ? (
            <Loader2 className="animate-spin mr-2" size={16} />
          ) : (
            <Download className="mr-2" size={16} />
          )}
          Esporta Excel
        </Button>
      </div>

      {/* Table */}
      <div className="border border-[#E7E5E4] rounded-lg overflow-hidden">
        {renderTable()}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, type: null, ids: [] })}
        onConfirm={confirmDelete}
        title="Conferma Eliminazione"
        message={deleteModal.message}
        confirmText="Elimina"
        cancelText="Annulla"
        variant="danger"
        loading={deleting}
      />
    </div>
  );
}

export default function ReportsPage({ getAuthHeaders, badgeCounts = {}, onMarkSeen }) {
  const currentYear = new Date().getFullYear();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Read initial tab from URL or default to "tassa-soggiorno"
  const initialTab = searchParams.get("tab") || "tassa-soggiorno";
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update activeTab when URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl && ["tassa-soggiorno", "importi-incassati", "occupazione", "guadagni"].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, location.search]);
  
  // Mark tax report as seen when visiting tassa-soggiorno tab
  useEffect(() => {
    if (activeTab === "tassa-soggiorno") {
      markTaxReportSeen();
    }
  }, [activeTab]);

  // Also mark as seen on initial mount if starting on tassa-soggiorno
  useEffect(() => {
    if (activeTab === "tassa-soggiorno") {
      markTaxReportSeen();
    }
  }, []);

  const markTaxReportSeen = async () => {
    try {
      await axios.post(`${API}/admin/reports/tax-mark-seen`, {}, getAuthHeaders());
      // Refresh badge counts in parent
      if (onMarkSeen) onMarkSeen();
    } catch (error) {
      console.error("Error marking tax report as seen:", error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Filters state
  const [filters, setFilters] = useState({
    year: currentYear,
    dateFrom: "",
    dateTo: ""
  });

  // Generate year options (last 5 years + next year)
  const yearOptions = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) {
    yearOptions.push(y);
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      if (key === "dateFrom" || key === "dateTo") {
        if (newFilters.dateFrom && newFilters.dateTo) {
          newFilters.year = "";
        }
      }
      
      if (key === "year" && value) {
        newFilters.dateFrom = "";
        newFilters.dateTo = "";
      }
      
      return newFilters;
    });
  };

  const clearDateRange = () => {
    setFilters(prev => ({
      ...prev,
      dateFrom: "",
      dateTo: "",
      year: currentYear
    }));
  };

  const reports = [
    {
      id: "tassa-soggiorno",
      title: "Tassa di Soggiorno",
      description: "Dati per la comunicazione ufficiale della tassa di soggiorno",
      icon: Building2
    },
    {
      id: "importi-incassati",
      title: "Importi",
      description: "Analisi degli importi incassati per soggiorno",
      icon: DollarSign
    },
    {
      id: "occupazione",
      title: "Occupazione",
      description: "Tasso di occupazione mensile",
      icon: BarChart3
    },
    {
      id: "guadagni",
      title: "Guadagni",
      description: "Guadagno mensile netto",
      icon: TrendingUp
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <h1 className="font-heading text-3xl">Report</h1>

      {/* Reports Tabs - ALIGNED TO PAYMENTS STYLE */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="admin-tabs-list">
          {reports.map(report => (
            <TabsTrigger
              key={report.id}
              value={report.id}
              className="admin-tab-trigger flex items-center gap-2"
            >
              <report.icon size={16} />
              {report.title}
              {report.id === "tassa-soggiorno" && badgeCounts.tax_report > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-bold bg-[#D4AF37] text-white rounded-full min-w-[18px] text-center">
                  {badgeCounts.tax_report}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Info sotto i tabs - cambia in base al tab attivo */}
        <PageInfoUnderTabs pageKey={`report_${activeTab === 'tassa-soggiorno' ? 'tassa_soggiorno' : activeTab === 'importi-incassati' ? 'importi' : activeTab === 'occupazione' ? 'occupazione' : 'guadagni'}`} />

        {/* Filters Section - BELOW TABS */}
        <div className="admin-card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={18} className="text-[#D4AF37]" />
            <span className="font-medium text-sm">Filtri</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {/* Year Filter */}
            <div>
              <label className="text-xs text-[#78716C] mb-1 block">Anno</label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange("year", e.target.value ? parseInt(e.target.value) : "")}
                className="w-full h-9 px-2 text-sm border border-[#E7E5E4] rounded-md bg-white"
                disabled={filters.dateFrom && filters.dateTo}
              >
                <option value="">Seleziona</option>
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {/* Date From */}
            <div>
              <label className="text-xs text-[#78716C] mb-1 block">Da</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="text-xs text-[#78716C] mb-1 block">A</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            {/* Clear Button */}
            <div className="flex items-end">
              {(filters.dateFrom || filters.dateTo) && (
                <Button
                  variant="outline"
                  onClick={clearDateRange}
                  size="sm"
                  className="w-full h-9"
                >
                  Reset
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Contents */}
        {reports.map(report => (
          <TabsContent key={report.id} value={report.id} className="mt-0">
            <div className="admin-card">
              <ReportTab
                title={report.title}
                description={report.description}
                icon={report.icon}
                reportType={report.id}
                getAuthHeaders={getAuthHeaders}
                filters={filters}
              />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
